import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import type { Answers, AnswerValue } from './catalogue';
import { type AnswerChanges, type ApplicationView, editable, intake } from './api';
import { overlapping, retryDelay, stillPending, withPending } from './drafts';

/** Typing stops for this long before the change is saved (SPEC, Saving). */
export const DEBOUNCE_MS = 1500;

export type SaveState = 'idle' | 'saving' | 'saved' | 'retrying' | 'locked';

/** Something to announce. The id changes every time, so a repeat is announced again. */
export interface Notice {
  id: number;
  tone: 'info' | 'success' | 'danger';
  text: string;
}

export interface Draft {
  application: ApplicationView;
  /** What the person sees: the saved answers with their unsaved edits on top. */
  answers: Answers;
  step: number;
  state: SaveState;
  /** True while anything typed has not yet reached the server. */
  unsaved: boolean;
  /** The applicant may change answers: a draft, or one reopened for more information. */
  editable: boolean;
  notice: Notice | null;
  setAnswer: (field: string, value: AnswerValue | null) => void;
  /** Moves to a step, saving everything first: the prototype's only save point. */
  goTo: (step: number) => void;
  giveConsent: () => void;
  /**
   * Sends the application, once everything typed has been saved, so what is sent
   * is what the person sees. Rejects with the server's refusal, or with
   * {@link UnsavedChanges} when the last edits could not be saved.
   */
  submit: () => Promise<void>;
}

/** The last edits have not reached the server, so sending now would leave them out. */
export class UnsavedChanges extends Error {
  constructor() {
    super('Your latest changes have not been saved yet.');
    this.name = 'UnsavedChanges';
  }
}

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * An application being filled in, saved as it is typed.
 *
 * Edits are kept as `pending` until the server has them. A save sends only those,
 * with the version it is based on, 1.5 seconds after typing stops or at once on a
 * step change. While one save is in flight, later edits wait for it and go next;
 * an edit made during a save is never marked saved by it (`stillPending`).
 *
 * When another tab or device saved first, the server refuses with the latest
 * draft. The edits here are laid on top of it and saved again, and the person is
 * told which of their fields the other session had also changed. Nothing typed is
 * dropped: on a network failure the save is retried with backoff for as long as
 * the page is open, and leaving with anything unsaved asks first.
 *
 * State lives in refs as well as in React state because the save loop runs in
 * timers and promises, which must read the latest values, not the ones from the
 * render that scheduled them.
 */
export function useDraft(
  initial: ApplicationView,
  /** How a field is named to the person: its question, not its id. */
  labelOf: (field: string) => string = (field) => field,
): Draft {
  const [application, setApplication] = useState(initial);
  const [pending, setPendingState] = useState<AnswerChanges>({});
  const [step, setStepState] = useState(initial.currentStep);
  const [state, setState] = useState<SaveState>(editable(initial.status) ? 'idle' : 'locked');
  const [notice, setNotice] = useState<Notice | null>(null);

  const savedRef = useRef(initial);
  const pendingRef = useRef<AnswerChanges>({});
  const stepRef = useRef(initial.currentStep);
  const consentRef = useRef(false);
  const inFlight = useRef(false);
  const again = useRef(false);
  const attempt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noticeId = useRef(0);
  // The save loop schedules itself (a retry, a follow-up save); through a ref, so it
  // always runs the current flush.
  const flushRef = useRef<() => Promise<void>>(async () => {});

  const announce = useCallback((tone: Notice['tone'], text: string) => {
    noticeId.current += 1;
    setNotice({ id: noticeId.current, tone, text });
  }, []);

  const setSaved = useCallback((view: ApplicationView) => {
    savedRef.current = view;
    setApplication(view);
  }, []);

  const setPending = useCallback((next: AnswerChanges) => {
    pendingRef.current = next;
    setPendingState(next);
  }, []);

  const cancelTimer = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const flush = useCallback(async (): Promise<void> => {
    cancelTimer();
    if (inFlight.current) {
      again.current = true;
      return;
    }
    const base = savedRef.current;
    if (!editable(base.status)) return;
    const sent = pendingRef.current;
    const sentStep = stepRef.current;
    const consent = consentRef.current && base.consentedAt === null;
    if (Object.keys(sent).length === 0 && sentStep === base.currentStep && !consent) return;

    inFlight.current = true;
    setState('saving');
    try {
      const view = await intake.save(base.applicationId, {
        version: base.version,
        currentStep: sentStep,
        answers: sent,
        ...(consent ? { consent: true } : {}),
      });
      setSaved(view);
      setPending(stillPending(pendingRef.current, sent));
      attempt.current = 0;
      setState('saved');
      announce('success', 'Progress saved');
    } catch (error) {
      if (error instanceof ApiError && error.status === 409 && error.code === 'version_conflict') {
        const latest = error.body?.latest as ApplicationView;
        const clashes = overlapping(base.answers, latest.answers, pendingRef.current);
        setSaved(latest);
        again.current = true;
        announce(
          'info',
          clashes.length > 0
            ? `Changed in another tab or on another device as well: ${clashes.map(labelOf).join('; ')}. Your version has been kept.`
            : 'Answers saved in another tab or on another device have been brought in.',
        );
      } else if (error instanceof ApiError && error.status === 409 && error.code === 'invalid_state') {
        setSaved(error.body?.latest as ApplicationView);
        const lost = Object.keys(pendingRef.current).length;
        setPending({});
        setState('locked');
        announce(
          'danger',
          lost > 0
            ? 'This application has been submitted, so the last changes here could not be saved.'
            : 'This application has been submitted and can no longer be changed.',
        );
      } else if (error instanceof ApiError && (error.status === 400 || error.status === 404)) {
        // Neither can succeed by retrying; say so rather than retry for ever.
        setPending(stillPending(pendingRef.current, sent));
        setState('idle');
        announce('danger', `${error.message} Quote request ${error.requestId ?? 'unknown'} if you report it.`);
      } else if (error instanceof ApiError && error.status === 401) {
        // The session ended; the provider signs out and the page asks to sign in.
        // Unsaved edits are lost with the page, which the leave warning covers.
        setState('idle');
      } else {
        setState('retrying');
        announce('danger', 'Could not save — retrying');
        const delay = retryDelay(attempt.current);
        attempt.current += 1;
        timer.current = setTimeout(() => void flushRef.current(), delay);
      }
    } finally {
      inFlight.current = false;
      if (again.current) {
        again.current = false;
        void flushRef.current();
      }
    }
  }, [announce, labelOf, setPending, setSaved]);

  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  const setAnswer = useCallback(
    (field: string, value: AnswerValue | null) => {
      if (!editable(savedRef.current.status)) return;
      setPending({ ...pendingRef.current, [field]: value });
      cancelTimer();
      timer.current = setTimeout(() => void flush(), DEBOUNCE_MS);
    },
    [flush, setPending],
  );

  const goTo = useCallback(
    (next: number) => {
      stepRef.current = next;
      setStepState(next);
      void flush();
    },
    [flush],
  );

  const giveConsent = useCallback(() => {
    consentRef.current = true;
    void flush();
  }, [flush]);

  const submit = useCallback(async (): Promise<void> => {
    for (let waited = 0; ; waited += 1) {
      const settled =
        !inFlight.current &&
        Object.keys(pendingRef.current).length === 0 &&
        stepRef.current === savedRef.current.currentStep;
      if (settled) break;
      // About five seconds: long enough for a save, short enough to say so.
      if (waited >= 50) throw new UnsavedChanges();
      if (!inFlight.current) void flush();
      await pause(100);
    }
    const base = savedRef.current;
    try {
      setSaved(await intake.submit(base.applicationId, base.version));
      setState('locked');
    } catch (error) {
      // Stale or already sent: show what the server holds, then say why.
      if (error instanceof ApiError && error.status === 409 && error.body?.latest) {
        setSaved(error.body.latest as ApplicationView);
        if (error.code === 'invalid_state') setState('locked');
      }
      throw error;
    }
  }, [flush, setSaved]);

  // Ask before leaving with anything unsaved. Browsers show their own sentence.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (Object.keys(pendingRef.current).length > 0 || inFlight.current) event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      cancelTimer();
    };
  }, []);

  const answers = useMemo(() => withPending(application.answers, pending), [application.answers, pending]);
  const unsaved = Object.keys(pending).length > 0 || state === 'saving' || state === 'retrying';

  return {
    application,
    answers,
    step,
    state,
    unsaved,
    editable: editable(application.status),
    notice,
    setAnswer,
    goTo,
    giveConsent,
    submit,
  };
}
